/// <reference types="vite/client" />
/* =====================================================================
 * UIStore — 다크모드 · 토스트 · 배경화면 (경량 전용)
 *
 * entry 청크에 포함되므로 외부 의존성을 최소화합니다.
 * chatStore는 이 스토어에 위임하며, 하위 호환 프록시를 유지합니다.
 * ===================================================================== */

import { create } from 'zustand';
import { fetchWallpaperFromCloud, saveWallpaperToCloud } from '@auw/api-client';
import { fetchThemeFromCloud, saveThemeToCloud } from '@auw/api-client';
import { getActiveAccountId } from '@auw/api-client';
import type { SettingsTab } from './types';
import {
  activeTenantKey as tenantKey,
  getActiveTenantManifest,
  resolveSystemWallpaper,
} from '@auw/tenant';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  description?: string;
  duration?: number;
}

export type WallpaperSource = 'custom' | 'system-dark' | 'system-light';

export interface WallpaperConfig {
  imageData: string;
  opacity: number;
  /** 적용 페이지 목록 */
  pages: string[];
  /** 전체 페이지 적용 여부 */
  applyToAll?: boolean;
  /** 배경 소스 종류 */
  source?: WallpaperSource;
}

export interface SettingsModalRequest {
  requestId: number;
  initialTab?: SettingsTab;
  focusSection?: 'interests';
}

interface UIState {
  darkMode: boolean;
  toasts: Toast[];
  wallpaper: WallpaperConfig | null;
  wallpaperUpdatedAt: number;
  wallpaperAccountId: string | null;
  wallpaperExplicit: boolean;
  settingsModalOpen: boolean;
  settingsModalRequest: SettingsModalRequest | null;
  tokenExhaustedModalOpen: boolean;
  tokenExhaustedTrialEndsAt: string | null;

  setDarkMode: (darkMode: boolean) => void;
  toggleDarkMode: () => void;
  addToast: (t: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  setSettingsModalOpen: (
    open: boolean,
    payload?: Omit<SettingsModalRequest, 'requestId'>,
  ) => void;
  openSettingsModal: (payload?: Omit<SettingsModalRequest, 'requestId'>) => void;
  openTokenExhaustedModal: (trialEndsAt?: string | null) => void;
  closeTokenExhaustedModal: () => void;
  setWallpaper: (config: WallpaperConfig | null) => void;
  updateWallpaper: (partial: Partial<WallpaperConfig>) => void;
  applyWallpaperSnapshot: (config: WallpaperConfig | null, updatedAt: number) => void;
  syncWallpaperForActiveAccount: () => void;
  hydrateWallpaperFromCloud: () => Promise<void>;
  flushWallpaperToCloud: (options?: { keepalive?: boolean }) => Promise<void>;

  /* 테마(다크/라이트) — wallpaper 와 동일하게 "계정 단위 로컬 + 클라우드
   * last-write-wins" 로 동기화한다. 단일 전역 키를 쓰면 같은 브라우저에서
   * 유저가 바뀔 때 이전 유저의 테마가 남는 누수가 생기므로 accountId 로
   * 스코프를 나눈다. */
  darkModeUpdatedAt: number;
  darkModeAccountId: string | null;
  applyThemeSnapshot: (darkMode: boolean, updatedAt: number) => void;
  syncDarkModeForActiveAccount: () => void;
  hydrateThemeFromCloud: () => Promise<void>;
  flushThemeToCloud: (options?: { keepalive?: boolean }) => Promise<void>;
}

interface WallpaperPayloadV1 {
  version: 1;
  wallpaper: {
    image_data: string;
    opacity: number;
    pages: string[];
    apply_to_all?: boolean;
    source?: WallpaperSource;
  } | null;
}

interface RestoredWallpaperState {
  config: WallpaperConfig | null;
  updatedAt: number;
  accountId: string | null;
  explicit: boolean;
}

/** Legacy(계정 구분 없는) 다크모드 키. 마이그레이션/하위호환용으로만 둔다. */
const DARK_MODE_STORAGE_KEY = tenantKey('dark');
/** 기본 테마 — 온보딩 및 신규 계정 기본값과 동일하게 라이트. */
const DEFAULT_DARK_MODE = false;

/** 다크모드를 accountId 단위로 분리 저장 (wallpaper 키 스코프와 동일 컨벤션). */
function getDarkModeStorageKey(accountId: string | null): string {
  return `${DARK_MODE_STORAGE_KEY}:${accountId || 'anonymous'}`;
}

const WALLPAPER_META_KEY_PREFIX = tenantKey('wallpaper');
const WALLPAPER_IMG_KEY_PREFIX = tenantKey('wallpaper_img');
const LEGACY_WALLPAPER_META_KEY = tenantKey('wallpaper');
const LEGACY_WALLPAPER_IMG_KEY = tenantKey('wallpaper_img');
const SYSTEM_WALLPAPER_ASSET_VERSION = '20260315-system-bg-v1';

/**
 * Bundled fallbacks shipped with every build under
 * `${BASE_URL}assets/system_bg_*.webp`. Kept here so the wallpaper
 * code path keeps working when no tenant manifest has been set
 * (early bootstrap, unit tests, …) — `getSystemWallpaperAssetSrc`
 * defers to `resolveSystemWallpaper(manifest, theme, fallback)` once
 * a manifest is available so tenants can swap the imagery without
 * touching the cache-busting query string.
 */
const DEFAULT_SYSTEM_WALLPAPER_SRC = {
  'system-dark': `${import.meta.env.BASE_URL}assets/system_bg_dark.webp?v=${SYSTEM_WALLPAPER_ASSET_VERSION}`,
  'system-light': `${import.meta.env.BASE_URL}assets/system_bg_light.webp?v=${SYSTEM_WALLPAPER_ASSET_VERSION}`,
} satisfies Record<Exclude<WallpaperSource, 'custom'>, string>;

let wallpaperCloudSaveTimer: number | null = null;
let themeCloudSaveTimer: number | null = null;

function createToastId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function clampOpacity(value: unknown, fallback = 30): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function normalizePages(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (!trimmed) continue;
    out.add(trimmed);
  }
  return [...out];
}

function normalizeApplyToAll(rawValue: unknown, pages: string[]): boolean {
  if (typeof rawValue === 'boolean') return rawValue;
  // 레거시 저장값은 빈 배열을 "전체 적용"으로 사용했다.
  return pages.length === 0;
}

function normalizeWallpaperSource(rawValue: unknown, imageData: string): WallpaperSource {
  if (rawValue === 'system-dark' || rawValue === 'system-light' || rawValue === 'custom') {
    return rawValue;
  }
  if (imageData.includes('system_bg_dark.webp')) return 'system-dark';
  if (imageData.includes('system_bg_light.webp')) return 'system-light';
  return 'custom';
}

interface StoredThemeState {
  darkMode: boolean;
  updatedAt: number;
}

function coerceNonNegInt(value: unknown): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * 계정 스코프 키에서 테마 상태를 읽는다. 스코프 키가 없으면(=해당 유저가
 * 이 브라우저에서 아직 설정한 적 없음) 다른 유저의 값을 물려받지 않도록
 * 기본값(다크)을 돌려준다 — 로그인 후 ``hydrateThemeFromCloud`` 가
 * 서버에 저장된 유저별 테마로 덮어쓴다. 과거 단일 'true'/'false' 포맷도
 * 관용적으로 파싱한다.
 */
function readStoredThemeState(
  accountId: string | null = getActiveAccountId(),
): StoredThemeState {
  try {
    const raw = localStorage.getItem(getDarkModeStorageKey(accountId));
    if (raw !== null) {
      if (raw === 'true' || raw === 'false') {
        return { darkMode: raw !== 'false', updatedAt: 0 };
      }
      const parsed = JSON.parse(raw) as Partial<StoredThemeState>;
      return {
        darkMode:
          typeof parsed.darkMode === 'boolean' ? parsed.darkMode : DEFAULT_DARK_MODE,
        updatedAt: coerceNonNegInt(parsed.updatedAt),
      };
    }
  } catch {
    // ignore storage access / parse errors
  }
  return { darkMode: DEFAULT_DARK_MODE, updatedAt: 0 };
}

function persistThemeState(
  accountId: string | null,
  darkMode: boolean,
  updatedAt: number,
): void {
  try {
    localStorage.setItem(
      getDarkModeStorageKey(accountId),
      JSON.stringify({ darkMode, updatedAt: coerceNonNegInt(updatedAt) }),
    );
  } catch {
    // ignore storage access errors
  }
}

interface ThemePayloadV1 {
  version: 1;
  darkMode: boolean;
}

function toThemePayload(darkMode: boolean): ThemePayloadV1 {
  return { version: 1, darkMode };
}

function fromThemePayload(payload: unknown): boolean | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.darkMode === 'boolean') return record.darkMode;
  return null;
}

function clearPendingThemeCloudSave(): void {
  if (themeCloudSaveTimer === null) return;
  window.clearTimeout(themeCloudSaveTimer);
  themeCloudSaveTimer = null;
}

/**
 * Specialized 에이전트(매니페스트 `specializedAgents`) 의 wallpaper
 * 페이지 id 컨벤션. WorkspaceMode union 과 namespace 가 겹치지 않도록
 * `specialized:` prefix 를 붙인다. `wallpaper.pages` 에 동일 문자열이
 * 저장되며, specialized 페이지 본체(예: CADAgentPage) 에서
 * `pages.includes(specializedWallpaperPageId(id))` 로 매칭한다.
 */
export function specializedWallpaperPageId(specializedAgentId: string): string {
  return `specialized:${specializedAgentId}`;
}

export function getSystemWallpaperAssetSrc(source: Exclude<WallpaperSource, 'custom'>): string {
  const fallback = DEFAULT_SYSTEM_WALLPAPER_SRC[source];
  // Pre-tenant-bootstrap callers (test harnesses, early module
  // evaluation) keep getting the bundled defaults instead of
  // crashing — `getActiveTenantManifest` throws when called before
  // `setActiveTenantManifest`.
  let manifest;
  try {
    manifest = getActiveTenantManifest();
  } catch {
    return fallback;
  }
  const theme = source === 'system-dark' ? 'dark' : 'light';
  return resolveSystemWallpaper(manifest, theme, fallback);
}

export function createSystemWallpaper(
  source: Exclude<WallpaperSource, 'custom'>,
  partial: Partial<WallpaperConfig> = {},
): WallpaperConfig {
  const pages = normalizePages(partial.pages);
  return {
    imageData: getSystemWallpaperAssetSrc(source),
    opacity: clampOpacity(partial.opacity, 100),
    pages,
    applyToAll: normalizeApplyToAll(partial.applyToAll, pages),
    source,
  };
}

export function resolveEffectiveWallpaperConfig(params: {
  wallpaper: WallpaperConfig | null;
  wallpaperExplicit: boolean;
  darkMode: boolean;
  accountId?: string | null;
}): WallpaperConfig | null {
  if (params.wallpaper) return params.wallpaper;
  const accountId = params.accountId ?? getActiveAccountId();
  if (params.wallpaperExplicit || !accountId) return null;
  return createSystemWallpaper(params.darkMode ? 'system-dark' : 'system-light');
}

function normalizeWallpaperConfig(raw: WallpaperConfig | null): WallpaperConfig | null {
  if (!raw) return null;
  if (typeof raw.imageData !== 'string' || !raw.imageData.trim()) return null;
  const pages = normalizePages(raw.pages);
  return {
    imageData: raw.imageData,
    opacity: clampOpacity(raw.opacity, 30),
    pages,
    applyToAll: normalizeApplyToAll(raw.applyToAll, pages),
    source: normalizeWallpaperSource(raw.source, raw.imageData),
  };
}

function isSameWallpaperConfig(a: WallpaperConfig | null, b: WallpaperConfig | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.imageData === b.imageData
    && a.opacity === b.opacity
    && Boolean(a.applyToAll) === Boolean(b.applyToAll)
    && (a.source || 'custom') === (b.source || 'custom')
    && a.pages.join(',') === b.pages.join(',')
  );
}

function clearPendingWallpaperCloudSave(): void {
  if (wallpaperCloudSaveTimer === null) return;
  window.clearTimeout(wallpaperCloudSaveTimer);
  wallpaperCloudSaveTimer = null;
}

function getWallpaperStorageKeySuffix(accountId: string | null): string {
  return accountId || 'anonymous';
}

function getWallpaperStorageKeys(accountId: string | null): { meta: string; img: string } {
  const suffix = getWallpaperStorageKeySuffix(accountId);
  return {
    meta: `${WALLPAPER_META_KEY_PREFIX}:${suffix}`,
    img: `${WALLPAPER_IMG_KEY_PREFIX}:${suffix}`,
  };
}

function clearLegacyWallpaperStorage(): void {
  try {
    localStorage.removeItem(LEGACY_WALLPAPER_META_KEY);
    localStorage.removeItem(LEGACY_WALLPAPER_IMG_KEY);
  } catch {
    // ignore storage access errors
  }
}

function restoreWallpaper(accountId: string | null = getActiveAccountId()): RestoredWallpaperState {
  try {
    const keys = getWallpaperStorageKeys(accountId);
    const raw = localStorage.getItem(keys.meta);
    const img = localStorage.getItem(keys.img);
    if (!raw) return { config: null, updatedAt: 0, accountId, explicit: false };
    const parsed = JSON.parse(raw) as {
      opacity?: unknown;
      pages?: unknown;
      updatedAt?: unknown;
      applyToAll?: unknown;
      source?: unknown;
      deleted?: unknown;
    };
    const updatedAtNum = Number(parsed.updatedAt);
    const updatedAt = Number.isFinite(updatedAtNum) ? Math.max(0, Math.floor(updatedAtNum)) : 0;
    if (parsed.deleted === true) {
      return { config: null, updatedAt, accountId, explicit: true };
    }
    if (!img) {
      return { config: null, updatedAt, accountId, explicit: true };
    }
    const config = normalizeWallpaperConfig({
      imageData: img,
      opacity: clampOpacity(parsed.opacity, 30),
      pages: normalizePages(parsed.pages),
      applyToAll: typeof parsed.applyToAll === 'boolean' ? parsed.applyToAll : undefined,
      source: parsed.source as WallpaperSource | undefined,
    });
    if (!config) return { config: null, updatedAt, accountId, explicit: true };
    return {
      config,
      updatedAt,
      accountId,
      explicit: true,
    };
  } catch {
    return { config: null, updatedAt: 0, accountId, explicit: false };
  }
}

function persistWallpaper(
  accountId: string | null,
  config: WallpaperConfig | null,
  updatedAt: number,
  imgChanged = false,
): void {
  const keys = getWallpaperStorageKeys(accountId);
  if (config) {
    localStorage.setItem(
      keys.meta,
      JSON.stringify({
        opacity: config.opacity,
        pages: config.pages,
        applyToAll: Boolean(config.applyToAll),
        source: config.source || 'custom',
        updatedAt,
        deleted: false,
      }),
    );
    if (imgChanged) localStorage.setItem(keys.img, config.imageData);
  } else {
    localStorage.setItem(
      keys.meta,
      JSON.stringify({
        updatedAt,
        deleted: true,
      }),
    );
    localStorage.removeItem(keys.img);
  }
}

function readActiveWallpaperState(): Pick<UIState, 'wallpaper' | 'wallpaperUpdatedAt' | 'wallpaperAccountId' | 'wallpaperExplicit'> {
  clearLegacyWallpaperStorage();
  const restored = restoreWallpaper();
  return {
    wallpaper: restored.config,
    wallpaperUpdatedAt: restored.updatedAt,
    wallpaperAccountId: restored.accountId,
    wallpaperExplicit: restored.explicit,
  };
}

function toWallpaperPayload(config: WallpaperConfig | null): WallpaperPayloadV1 {
  if (!config) {
    return { version: 1, wallpaper: null };
  }
  return {
    version: 1,
    wallpaper: {
      image_data: config.imageData,
      opacity: clampOpacity(config.opacity, 30),
      pages: normalizePages(config.pages),
      apply_to_all: normalizeApplyToAll(config.applyToAll, config.pages),
      source: normalizeWallpaperSource(config.source, config.imageData),
    },
  };
}

function fromWallpaperPayload(payload: unknown): WallpaperConfig | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  if (Number(record.version) !== 1) return null;
  const wallpaperRaw = record.wallpaper;
  if (wallpaperRaw == null) return null;
  if (typeof wallpaperRaw !== 'object') return null;
  const wallpaper = wallpaperRaw as Record<string, unknown>;
  const imageData = typeof wallpaper.image_data === 'string' ? wallpaper.image_data : '';
  const opacity = clampOpacity(wallpaper.opacity, 30);
  const pages = normalizePages(wallpaper.pages);
  const applyToAll = normalizeApplyToAll(wallpaper.apply_to_all, pages);
  const source = normalizeWallpaperSource(wallpaper.source, imageData);
  return normalizeWallpaperConfig({
    imageData,
    opacity,
    pages,
    applyToAll,
    source,
  });
}

export const useUIStore = create<UIState>((set, get) => ({
  ...(() => {
    return readActiveWallpaperState();
  })(),
  ...(() => {
    const theme = readStoredThemeState();
    return {
      darkMode: theme.darkMode,
      darkModeUpdatedAt: theme.updatedAt,
      darkModeAccountId: getActiveAccountId(),
    };
  })(),
  toasts: [],
  settingsModalOpen: false,
  settingsModalRequest: null,
  tokenExhaustedModalOpen: false,
  tokenExhaustedTrialEndsAt: null,

  setDarkMode: (darkMode) => {
    get().syncDarkModeForActiveAccount();
    const accountId = getActiveAccountId();
    let changed = false;
    set((state) => {
      if (state.darkMode === darkMode) return {};
      changed = true;
      const nextUpdatedAt = Math.max(Date.now(), state.darkModeUpdatedAt + 1);
      persistThemeState(accountId, darkMode, nextUpdatedAt);
      return {
        darkMode,
        darkModeUpdatedAt: nextUpdatedAt,
        darkModeAccountId: accountId,
      };
    });
    if (!changed) return;
    clearPendingThemeCloudSave();
    themeCloudSaveTimer = window.setTimeout(() => {
      void get().flushThemeToCloud();
    }, 900);

    // 시스템 배경(system-light/system-dark)을 쓰는 중이면 새 테마에 맞는
    // 변형으로 함께 교체한다. (명시 설정이 없는 계정은
    // resolveEffectiveWallpaperConfig 가 darkMode 기준으로 동적으로 고르므로
    // 여기서 손댈 필요 없음 — 커스텀 이미지는 사용자가 고른 것이니 유지.)
    const currentWallpaper = get().wallpaper;
    const desiredSource: WallpaperSource = darkMode ? 'system-dark' : 'system-light';
    if (
      currentWallpaper
      && (currentWallpaper.source === 'system-dark' || currentWallpaper.source === 'system-light')
      && currentWallpaper.source !== desiredSource
    ) {
      get().setWallpaper(createSystemWallpaper(desiredSource, {
        opacity: currentWallpaper.opacity,
        pages: currentWallpaper.pages,
        applyToAll: currentWallpaper.applyToAll,
      }));
    }
  },

  toggleDarkMode: () => get().setDarkMode(!get().darkMode),

  addToast: (t) => {
    const id = createToastId();
    const toast: Toast = { ...t, id };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    return id;
  },

  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  setSettingsModalOpen: (open, payload) =>
    set((state) => ({
      settingsModalOpen: open,
      settingsModalRequest: open
        ? {
          requestId: Date.now(),
          initialTab: payload?.initialTab,
          focusSection: payload?.focusSection,
        }
        : state.settingsModalRequest,
    })),

  openSettingsModal: (payload) => get().setSettingsModalOpen(true, payload),

  openTokenExhaustedModal: (trialEndsAt) =>
    set({ tokenExhaustedModalOpen: true, tokenExhaustedTrialEndsAt: trialEndsAt ?? null }),

  closeTokenExhaustedModal: () =>
    set({ tokenExhaustedModalOpen: false }),

  syncWallpaperForActiveAccount: () => {
    const next = readActiveWallpaperState();
    const current = get();
    if (
      current.wallpaperAccountId === next.wallpaperAccountId
      && current.wallpaperUpdatedAt === next.wallpaperUpdatedAt
      && current.wallpaperExplicit === next.wallpaperExplicit
      && isSameWallpaperConfig(current.wallpaper, next.wallpaper)
    ) {
      return;
    }
    clearPendingWallpaperCloudSave();
    set(next);
  },

  setWallpaper: (config) => {
    get().syncWallpaperForActiveAccount();
    const normalized = normalizeWallpaperConfig(config);
    const accountId = getActiveAccountId();
    set((s) => {
      const nextUpdatedAt = Math.max(Date.now(), s.wallpaperUpdatedAt + 1);
      persistWallpaper(accountId, normalized, nextUpdatedAt, true);
      return {
        wallpaper: normalized,
        wallpaperUpdatedAt: nextUpdatedAt,
        wallpaperAccountId: accountId,
        wallpaperExplicit: true,
      };
    });
    clearPendingWallpaperCloudSave();
    wallpaperCloudSaveTimer = window.setTimeout(() => {
      void get().flushWallpaperToCloud();
    }, 900);
  },

  updateWallpaper: (partial) => {
    get().syncWallpaperForActiveAccount();
    const accountId = getActiveAccountId();
    set((s) => {
      const baseWallpaper = resolveEffectiveWallpaperConfig({
        wallpaper: s.wallpaper,
        wallpaperExplicit: s.wallpaperExplicit,
        darkMode: s.darkMode,
        accountId,
      });
      if (!baseWallpaper) return {};
      const next = normalizeWallpaperConfig({ ...baseWallpaper, ...partial });
      if (!next) return {};
      if (isSameWallpaperConfig(next, s.wallpaper) && s.wallpaperExplicit) {
        return {};
      }
      const nextUpdatedAt = Math.max(Date.now(), s.wallpaperUpdatedAt + 1);
      persistWallpaper(accountId, next, nextUpdatedAt, partial.imageData !== undefined);
      clearPendingWallpaperCloudSave();
      wallpaperCloudSaveTimer = window.setTimeout(() => {
        void get().flushWallpaperToCloud();
      }, 900);
      return {
        wallpaper: next,
        wallpaperUpdatedAt: nextUpdatedAt,
        wallpaperAccountId: accountId,
        wallpaperExplicit: true,
      };
    });
  },

  applyWallpaperSnapshot: (config, updatedAt) => {
    get().syncWallpaperForActiveAccount();
    const normalized = normalizeWallpaperConfig(config);
    const normalizedUpdatedAt = Number.isFinite(updatedAt)
      ? Math.max(0, Math.floor(updatedAt))
      : 0;
    const accountId = getActiveAccountId();
    persistWallpaper(accountId, normalized, normalizedUpdatedAt, true);
    set({
      wallpaper: normalized,
      wallpaperUpdatedAt: normalizedUpdatedAt,
      wallpaperAccountId: accountId,
      wallpaperExplicit: true,
    });
  },

  hydrateWallpaperFromCloud: async () => {
    get().syncWallpaperForActiveAccount();
    const accountId = getActiveAccountId();
    if (!accountId) return;

    try {
      const remote = await fetchWallpaperFromCloud();
      const remoteUpdatedAt = Math.max(0, Number(remote.updated_at || 0));
      const remoteConfig = fromWallpaperPayload(remote.payload);
      const { wallpaper, wallpaperUpdatedAt } = get();

      if (remoteUpdatedAt > wallpaperUpdatedAt) {
        get().applyWallpaperSnapshot(remoteConfig, remoteUpdatedAt);
      } else if (wallpaperUpdatedAt > remoteUpdatedAt) {
        await get().flushWallpaperToCloud();
      } else if (!wallpaper && remoteConfig) {
        get().applyWallpaperSnapshot(remoteConfig, remoteUpdatedAt);
      } else if (wallpaper && !remoteConfig) {
        await get().flushWallpaperToCloud();
      }
    } catch {
      // 네트워크 오류 시 로컬 상태 유지
    }
  },

  flushWallpaperToCloud: async (options) => {
    get().syncWallpaperForActiveAccount();
    const accountId = getActiveAccountId();
    if (!accountId) return;

    const current = get();
    let nextUpdatedAt = Math.max(0, Number(current.wallpaperUpdatedAt || 0));
    if (nextUpdatedAt <= 0 && current.wallpaper) {
      nextUpdatedAt = Date.now();
      persistWallpaper(accountId, current.wallpaper, nextUpdatedAt, false);
      set({ wallpaperUpdatedAt: nextUpdatedAt, wallpaperAccountId: accountId });
    }
    if (nextUpdatedAt <= 0 && !current.wallpaper) return;

    try {
      const saved = await saveWallpaperToCloud(
        toWallpaperPayload(get().wallpaper),
        nextUpdatedAt,
        { keepalive: Boolean(options?.keepalive) },
      );
      const savedUpdatedAt = Math.max(nextUpdatedAt, Number(saved.updated_at || 0));
      if (savedUpdatedAt > get().wallpaperUpdatedAt) {
        persistWallpaper(accountId, get().wallpaper, savedUpdatedAt, false);
        set({ wallpaperUpdatedAt: savedUpdatedAt, wallpaperAccountId: accountId });
      }
    } catch {
      // 실패 시 다음 사이클에서 재시도
    }
  },

  applyThemeSnapshot: (darkMode, updatedAt) => {
    get().syncDarkModeForActiveAccount();
    const accountId = getActiveAccountId();
    const normalizedUpdatedAt = coerceNonNegInt(updatedAt);
    persistThemeState(accountId, darkMode, normalizedUpdatedAt);
    set({
      darkMode,
      darkModeUpdatedAt: normalizedUpdatedAt,
      darkModeAccountId: accountId,
    });
  },

  syncDarkModeForActiveAccount: () => {
    const accountId = getActiveAccountId();
    if (get().darkModeAccountId === accountId) return;
    const next = readStoredThemeState(accountId);
    clearPendingThemeCloudSave();
    set({
      darkMode: next.darkMode,
      darkModeUpdatedAt: next.updatedAt,
      darkModeAccountId: accountId,
    });
  },

  hydrateThemeFromCloud: async () => {
    get().syncDarkModeForActiveAccount();
    const accountId = getActiveAccountId();
    if (!accountId) return;

    try {
      const remote = await fetchThemeFromCloud();
      const remoteUpdatedAt = Math.max(0, Number(remote.updated_at || 0));
      const remoteDarkMode = fromThemePayload(remote.payload);
      const { darkModeUpdatedAt } = get();

      if (remoteDarkMode === null) {
        // 서버에 아직 테마 없음 → 로컬에 명시적 선택이 있으면 올린다.
        if (darkModeUpdatedAt > 0) await get().flushThemeToCloud();
        return;
      }

      if (remoteUpdatedAt > darkModeUpdatedAt) {
        get().applyThemeSnapshot(remoteDarkMode, remoteUpdatedAt);
      } else if (darkModeUpdatedAt > remoteUpdatedAt) {
        await get().flushThemeToCloud();
      }
    } catch {
      // 네트워크 오류 시 로컬 상태 유지
    }
  },

  flushThemeToCloud: async (options) => {
    get().syncDarkModeForActiveAccount();
    const accountId = getActiveAccountId();
    if (!accountId) return;

    const current = get();
    const nextUpdatedAt = Math.max(0, Number(current.darkModeUpdatedAt || 0));
    // 명시적 선택 기록이 없으면(updatedAt<=0) 동기화 시작점을 만들지 않는다.
    if (nextUpdatedAt <= 0) return;

    try {
      const saved = await saveThemeToCloud(
        toThemePayload(get().darkMode),
        nextUpdatedAt,
        { keepalive: Boolean(options?.keepalive) },
      );
      const savedUpdatedAt = Math.max(nextUpdatedAt, Number(saved.updated_at || 0));
      if (savedUpdatedAt > get().darkModeUpdatedAt) {
        persistThemeState(accountId, get().darkMode, savedUpdatedAt);
        set({ darkModeUpdatedAt: savedUpdatedAt, darkModeAccountId: accountId });
      }
    } catch {
      // 실패 시 다음 사이클에서 재시도
    }
  },
}));
